import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, doesNotHaveExpand, patch, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { CartResult } from "@insite/client-framework/Services/CartService";
import { BillToModel, CartModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

export interface InvoicePaymentApiParameter extends ApiParameter {
    cart: Cart;
}

export type Cart = Omit<CartModel, "billTo" | "shipTo"> & {
    billToId?: string;
    shipToId?: string;
    collectionShipToLabel?: string;
    properties: {
    [key: string]: string;
    };
};

const InvoicePaymentUrl = "api/v2/invoicepayment";

export async function invoicePayment(parameter: InvoicePaymentApiParameter): Promise<ServiceResult<CartResult>> {
    const { cart } = parameter;
    const { billToId, shipToId } = cart;
    const patchModel: CartModel = {
        ...cart,
        billTo: billToId ? ({ id: billToId } as BillToModel) : null,
        shipTo: shipToId ? ({ id: shipToId } as ShipToModel) : null,
    };
    try {
        const cartModel = await patch<CartModel>(`${InvoicePaymentUrl}/current`, patchModel);
        const cartResult = cleanCart(cartModel, { expand: ["paymentOptions"] });
        return {
            successful: true,
            result: cartResult,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

function cleanCart(cartModel: CartModel, parameter?: { expand?: string[]; additionalExpands?: string[] }) {
    cartModel.orderDate = cartModel.orderDate! && new Date(cartModel.orderDate!);
    cartModel.requestedPickupDateDisplay =
        cartModel.requestedPickupDateDisplay! && new Date(cartModel.requestedPickupDateDisplay!);
    cartModel.requestedDeliveryDateDisplay =
        cartModel.requestedDeliveryDateDisplay! && new Date(cartModel.requestedDeliveryDateDisplay!);

    if (doesNotHaveExpand(parameter, "cartLines")) {
        delete cartModel.cartLines;
    }
    if (doesNotHaveExpand(parameter, "paymentOptions")) {
        delete cartModel.paymentMethod;
        delete cartModel.paymentOptions;
    }
    if (doesNotHaveExpand(parameter, "carriers")) {
        delete cartModel.carriers;
    }
    if (doesNotHaveExpand(parameter, "validation")) {
        delete cartModel.billTo?.validation;
        if (cartModel.shipTo) {
            cartModel.shipTo.validation = null;
        }
    }
    const cartResult = {
        cart: cartModel as Cart,
        billTo: cartModel.billTo ? cartModel.billTo : undefined,
        shipTo: cartModel.shipTo ? cartModel.shipTo : undefined,
    };

    if (cartModel.billTo) {
        cartResult.cart.billToId = cartModel.billTo.id;
        delete (cartResult.cart as CartModel).billTo;
    }

    if (cartModel.shipTo) {
        cartResult.cart.shipToId = cartModel.shipTo.id;
        delete (cartResult.cart as CartModel).shipTo;
    }

    return cartResult;
}